import React, { useState, useRef, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { cn } from "../../lib/cn";

type TooltipPlacement = "top" | "bottom" | "left" | "right";

interface TooltipProps {
  content: React.ReactNode;
  placement?: TooltipPlacement;
  delay?: number;
  children: React.ReactElement<{
    onMouseEnter?: React.MouseEventHandler;
    onMouseLeave?: React.MouseEventHandler;
    onFocus?: React.FocusEventHandler;
    onBlur?: React.FocusEventHandler;
    ref?: React.Ref<HTMLElement>;
  }>;
  disabled?: boolean;
}

interface Position {
  top: number;
  left: number;
}

const OFFSET = 8;

function computePosition(
  triggerRect: DOMRect,
  tooltipRect: DOMRect,
  placement: TooltipPlacement,
): Position {
  const scrollX = window.scrollX;
  const scrollY = window.scrollY;

  switch (placement) {
    case "top":
      return {
        top: triggerRect.top + scrollY - tooltipRect.height - OFFSET,
        left:
          triggerRect.left +
          scrollX +
          triggerRect.width / 2 -
          tooltipRect.width / 2,
      };
    case "bottom":
      return {
        top: triggerRect.bottom + scrollY + OFFSET,
        left:
          triggerRect.left +
          scrollX +
          triggerRect.width / 2 -
          tooltipRect.width / 2,
      };
    case "left":
      return {
        top:
          triggerRect.top +
          scrollY +
          triggerRect.height / 2 -
          tooltipRect.height / 2,
        left: triggerRect.left + scrollX - tooltipRect.width - OFFSET,
      };
    case "right":
      return {
        top:
          triggerRect.top +
          scrollY +
          triggerRect.height / 2 -
          tooltipRect.height / 2,
        left: triggerRect.right + scrollX + OFFSET,
      };
  }
}

function Tooltip({
  content,
  placement = "top",
  delay = 200,
  children,
  disabled = false,
}: TooltipProps) {
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState<Position>({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const show = useCallback(() => {
    if (disabled) return;
    timeoutRef.current = setTimeout(() => setVisible(true), delay);
  }, [delay, disabled]);

  const hide = useCallback(() => {
    clearTimeout(timeoutRef.current);
    setVisible(false);
  }, []);

  useEffect(() => {
    return () => clearTimeout(timeoutRef.current);
  }, []);

  useEffect(() => {
    if (!visible || !triggerRef.current || !tooltipRef.current) return;
    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    setPosition(computePosition(triggerRect, tooltipRect, placement));
  }, [visible, placement]);

  const child = React.Children.only(children);
  const trigger = React.cloneElement(child, {
    ref: triggerRef,
    onMouseEnter: (e: React.MouseEvent) => {
      show();
      child.props.onMouseEnter?.(e);
    },
    onMouseLeave: (e: React.MouseEvent) => {
      hide();
      child.props.onMouseLeave?.(e);
    },
    onFocus: (e: React.FocusEvent) => {
      show();
      child.props.onFocus?.(e);
    },
    onBlur: (e: React.FocusEvent) => {
      hide();
      child.props.onBlur?.(e);
    },
  } as Partial<typeof child.props>);

  const tooltip = visible
    ? createPortal(
        <div
          ref={tooltipRef}
          role="tooltip"
          style={{ top: position.top, left: position.left }}
          className={cn(
            "fixed z-[70] max-w-xs",
            "rounded-[var(--radius-md)] px-3 py-1.5",
            "bg-[var(--text-primary)] text-[var(--text-inverse)]",
            "text-xs font-medium leading-snug",
            "shadow-[var(--shadow-lg)]",
            "pointer-events-none select-none",
            "animate-[tooltip-in_var(--duration-fast)_var(--easing-entrance)]",
          )}
        >
          {content}
        </div>,
        document.body,
      )
    : null;

  return (
    <>
      {trigger}
      {tooltip}
    </>
  );
}

Tooltip.displayName = "Tooltip";

export { Tooltip };
export type { TooltipProps, TooltipPlacement };
